import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Music } from 'lucide-react';

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const marsLyrics = [
  {
    stanza: [
      'STIKES HARAPAN IBU JAMBI',
      'SEKOLAH TINGGI ILMU KESEHATAN',
      'TEMPAT KU MENUNTUT ILMU DAN BERKARYA',
      'MENUJU CITA-CITA YANG MULIA',
    ]
  },
  {
    stanza: [
      'STIKES HARAPAN IBU JAMBI',
      'MENCIPTAKAN INSAN YANG BERKWALITAS',
      'DALAM BIDANG PELAYANAN KESEHATAN',
      'BAGI NEGARA NUSA DAN BANGSA',
    ]
  },
  {
    label: 'REFF:',
    stanza: [
      'TRI DHARMA PERGURUAN TINGGI',
      'MENJADI PEDOMANKU TUK BERKARYA',
      'TIADA RAGU DAN TIADA BIMBANG',
      'DEMI MASA DEPANKU YANG GEMILANG',
    ]
  },
  {
    stanza: [
      'DENGAN SEMANGAT YANG MEMBARA',
      'MARILAH JUNJUNG ALMAMATER KITA',
      'STIKES HARAPAN IBU JAMBI TERCINTA',
      'SELALU TERDEPAN DAN JAYALAH SELAMANYA',
    ]
  },
];

const MarsStikes = () => (
  <div data-testid="mars-stikes-page">
    <PageHero title="Mars STIKES Harapan Ibu Jambi" breadcrumbs={['Tentang Kami', 'Mars STIKES HI']} />

    <div className="bg-[#F8F7FF] dark:bg-[#121020]">
      {/* YouTube Video */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white dark:bg-[#1A182E] rounded-3xl overflow-hidden shadow-xl border border-purple-100 dark:border-purple-900/20">
              {/* Video Header */}
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white">
                  <Music size={22} />
                </div>
                <div>
                  <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading">Mars STIKES Harapan Ibu Jambi</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Cipt: Iwen Bahroem</p>
                </div>
              </div>
              {/* YouTube Embed */}
              <div data-testid="youtube-embed" className="relative w-full aspect-video bg-black">
                <iframe
                  src="https://www.youtube.com/embed/J5wZUAhspBU"
                  title="Mars STIKES Harapan Ibu Jambi"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lyrics */}
      <section className="py-16 bg-white dark:bg-[#100E1C]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Lirik Mars STIKES HI</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Cipt: Iwen Bahroem</p>
          </motion.div>

          <div data-testid="lyrics-section" className="space-y-10">
            {marsLyrics.map((block, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                {i > 0 && (
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#7B2FBE]/30 to-transparent" />
                    <div className="w-2 h-2 rounded-full bg-[#7B2FBE]" />
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent via-[#7B2FBE]/30 to-transparent" />
                  </div>
                )}
                {block.label && (
                  <p className="text-center text-[#7B2FBE] dark:text-[#9333EA] font-bold text-sm uppercase tracking-widest mb-3">
                    {block.label}
                  </p>
                )}
                <div className="text-center space-y-2">
                  {block.stanza.map((line, j) => (
                    <p
                      key={j}
                      className="text-[#1A1A2E] dark:text-gray-200 font-medium leading-relaxed"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {line}
                    </p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer decoration */}
          <div className="mt-12 text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-[#7B2FBE]" />
              <Music size={16} className="text-[#7B2FBE]" />
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-[#7B2FBE]" />
            </div>
            <p className="text-xs text-gray-400 mt-3">© STIKES Harapan Ibu Jambi</p>
          </div>
        </div>
      </section>
    </div>
  </div>
);

export default MarsStikes;
